'use client'
import { useState } from 'react'
import Link from 'next/link'
import { Link2, QrCode, Check, Image as ImageIcon, ExternalLink } from 'lucide-react'

interface EventCardActionsProps {
  slug: string
  qrCodePng?: string | null
  isPast?: boolean
  photos?: any
  links?: any
}

export default function EventCardActions({ slug, qrCodePng, isPast, photos, links }: EventCardActionsProps) {
  const [copied, setCopied] = useState(false)
  const [showQr, setShowQr] = useState(false)

  const copyLink = () => {
    const url = `${window.location.origin}/event/${slug}`
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (isPast) {
    const hasPhotos = Array.isArray(photos) && photos.length > 0
    const hasLinks = Array.isArray(links) && links.length > 0
    const firstLink = hasLinks ? links[0] : null
    
    return (
      <div className="flex gap-2 mt-2">
        {hasPhotos && (
          <Link href={`/event/${slug}/photos`} className="flex-1 flex items-center justify-center gap-2 text-center text-white py-2.5 px-4 rounded-xl text-sm font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all" style={{ background: 'linear-gradient(90deg, #F39C12, #E67E22)' }}>
            <ImageIcon className="w-4 h-4" /> View Photos
          </Link>
        )}
        {hasLinks && (
          <a href={firstLink} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-2 text-center text-[#F39C12] py-2.5 px-4 rounded-xl text-sm font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all border border-[#F39C12]" style={{ background: 'rgba(243,156,18,0.1)' }}>
            <ExternalLink className="w-4 h-4" /> View Links
          </a>
        )}
        {!hasPhotos && !hasLinks && (
          <span className="text-white/50 text-sm italic w-full text-center block py-2">Event concluded</span>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 mt-2">
        <Link 
          href={`/event/${slug}`}
          className="flex-1 text-center text-white hover:text-white transition-all py-2.5 px-4 rounded-xl text-sm font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          style={{ background: 'linear-gradient(90deg, #F39C12, #E67E22)' }}
        >
          Join Event
        </Link>
        <button
          onClick={copyLink}
          className="p-2.5 rounded-xl border border-[rgba(255,255,255,0.1)] text-white/60 hover:text-[#F39C12] hover:border-[rgba(243,156,18,0.3)] hover:bg-[rgba(243,156,18,0.1)] transition-all"
          title="Copy Share Link"
        >
          {copied ? <Check className="w-5 h-5 text-green-400" /> : <Link2 className="w-5 h-5" />}
        </button>
        {qrCodePng && (
          <button
            onClick={() => setShowQr(!showQr)}
            className={`p-2.5 rounded-xl border transition-all ${showQr ? 'border-[rgba(243,156,18,0.5)] text-[#F39C12] bg-[rgba(243,156,18,0.15)]' : 'border-[rgba(255,255,255,0.1)] text-white/60 hover:text-[#F39C12] hover:border-[rgba(243,156,18,0.3)] hover:bg-[rgba(243,156,18,0.1)]'}`}
            title="View QR Code"
          >
            <QrCode className="w-5 h-5" />
          </button>
        )}
      </div>

      {showQr && qrCodePng && (
        <div className="mt-3 p-4 border border-[rgba(243,156,18,0.2)] rounded-xl shadow-inner text-center animate-in fade-in zoom-in duration-200"
             style={{ background: 'rgba(0,0,0,0.2)' }}>
          <img src={qrCodePng} alt="QR Code" className="w-40 h-40 mx-auto border-2 border-[rgba(243,156,18,0.5)] rounded-lg shadow-lg mb-3" />
          <a 
            href={qrCodePng} 
            download={`${slug}-qr.png`}
            className="text-xs text-white font-bold px-4 py-2 rounded-lg inline-block transition-all hover:scale-105 shadow-md"
            style={{ background: 'linear-gradient(90deg, #F39C12, #E67E22)' }}
          >
            Download QR
          </a>
        </div>
      )}
    </div>
  )
}
